import { useEffect, useState } from "react";
import { BellRing, ChevronDown, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const toKey = (b64) => {
  const raw = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

let installPrompt = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); installPrompt = e; });

export function useAdminPwa() {
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/admin-manifest.json";
      document.head.appendChild(link);
    }
    if (supported) navigator.serviceWorker.register("/admin-sw.js", { scope: "/" }).catch(() => {});
    return () => link.remove();
  }, []);
}

export default function AdminPushCard() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canInstall, setCanInstall] = useState(Boolean(installPrompt));
  const [open, setOpen] = useState(localStorage.getItem("admin_push_card_open") === "1");
  const toggle = () => setOpen((o) => { localStorage.setItem("admin_push_card_open", o ? "0" : "1"); return !o; });
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;

  useEffect(() => {
    if (supported) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
    const h = () => setCanInstall(true);
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const run = (fn) => async () => {
    setBusy(true);
    try { await fn(); } catch (err) {
      const blocked = err.name === "NotAllowedError" || /permission/i.test(err.message || "");
      toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : blocked ? "Notifications bloquées : autorisez-les dans les paramètres du site dans Chrome (icône cadenas)" : err.message || "Action impossible");
    } finally { setBusy(false); }
  };

  const enable = run(async () => {
    if ((await Notification.requestPermission()) !== "granted") throw new Error("Autorisez les notifications dans Chrome pour ce site");
    const reg = await navigator.serviceWorker.register("/admin-sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    const { data } = await api.get("/admin/push/public-key");
    const s = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.public_key) }));
    const j = s.toJSON();
    await api.post("/admin/push/subscriptions", { endpoint: j.endpoint, keys: j.keys, device: navigator.userAgent.slice(0, 120) });
    setSub(s);
    toast.success("Notifications activées sur cet appareil");
  });
  const disable = run(async () => {
    await api.delete("/admin/push/subscriptions", { data: { endpoint: sub.endpoint } });
    await sub.unsubscribe();
    setSub(null);
    toast.success("Notifications désactivées sur cet appareil");
  });
  const test = run(async () => { await api.post("/admin/push/test", { endpoint: sub.endpoint }); toast.success("Notification de test envoyée"); });
  const install = async () => { installPrompt?.prompt(); installPrompt = null; setCanInstall(false); };

  const btn = "flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60";
  return (
    <section data-testid="admin-push-card" className={`rounded-2xl border border-amber-800/15 bg-white shadow-sm ${open ? "p-6" : "px-6 py-3"}`}>
      <button type="button" data-testid="admin-push-toggle" aria-expanded={open} onClick={toggle} className="flex w-full flex-wrap items-center gap-2 text-left">
        <BellRing size={18} className="shrink-0 text-honey" />
        <span className={`font-serif font-semibold text-bark ${open ? "text-xl" : "text-base"}`}>Application mobile et notifications</span>
        {!open && <span data-testid="admin-push-status" className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${sub ? "bg-sage/15 text-sage" : "bg-amber-100 text-amber-deep"}`}>{sub ? "Notifications actives" : "Notifications inactives"}</span>}
        <ChevronDown size={18} className={`ml-auto shrink-0 text-bark-soft transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <>
      <p className="mt-1 text-sm text-bark-soft">Recevez une alerte sur ce téléphone pour chaque commande payée, commande par chèque, avis à valider et message de contact.</p>
      {!standalone && (
        <p className="mt-3 text-xs text-bark-soft">Installer l'application : {canInstall ? "utilisez le bouton ci-dessous." : "dans Chrome, menu ⋮ → « Ajouter à l'écran d'accueil » / « Installer l'application »."}</p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {canInstall && !standalone && <button data-testid="admin-pwa-install" onClick={install} className={`${btn} border border-amber-800/25 text-bark hover:border-amber-deep`}><Download size={15} /> Installer l'application</button>}
        {!supported ? (
          <p data-testid="admin-push-unsupported" className="text-sm text-terra">Ce navigateur ne gère pas les notifications push. Utilisez Chrome sur Android.</p>
        ) : sub ? (
          <>
            <span data-testid="admin-push-enabled" className="self-center text-sm font-medium text-sage">Notifications actives sur cet appareil</span>
            <button data-testid="admin-push-test" disabled={busy} onClick={test} className={`${btn} bg-amber-deep text-white hover:bg-honey`}>{busy && <Loader2 size={15} className="animate-spin" />} Envoyer un test</button>
            <button data-testid="admin-push-disable" disabled={busy} onClick={disable} className={`${btn} border border-amber-900/20 text-bark hover:border-terra hover:text-terra`}>Désactiver</button>
          </>
        ) : (
          <button data-testid="admin-push-enable" disabled={busy} onClick={enable} className={`${btn} bg-amber-deep text-white hover:bg-honey`}>{busy && <Loader2 size={15} className="animate-spin" />} Activer les notifications sur cet appareil</button>
        )}
      </div>
      </>}
    </section>
  );
}
